import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Brand } from '@fametrue/shared';
import { Accordion } from './Accordion';
import { BrandHeader } from './BrandHeader';
import { decisionSections, type DecisionSection } from './decisionCards';
import { SIGNAL_QUALITY } from './palette';

/**
 * Level 2 — Decision.
 *
 * Il guscio: testata, riga fonti, le quattro sezioni, il disclaimer. Quali card
 * esistono, in che ordine e con che numero lo decide `decisionSections()` in
 * `decisionCards.tsx`, perché il contatore dei numeri attraversa tutte e
 * quattro le sezioni e non può vivere dentro una di esse.
 *
 * Titoli e sottotitoli delle dieci card sono stringhe d'interfaccia e seguono
 * la lingua (16/10, Level 2 punti 1 e 2): dalla 06 alla 10 arrivavano dal JSON
 * e restavano in italiano anche in inglese. I campi `title` e `subtitle`
 * restano nel contratto dati, validati e conservati, e non si leggono più.
 */
export function DecisionView({ brand }: { brand: Brand }): ReactNode {
  const { t } = useTranslation();
  const signal = SIGNAL_QUALITY[brand.signalQuality] ?? SIGNAL_QUALITY['partial']!;

  const [diagnosis, platformDetail, economicExposure, remediation] = decisionSections(brand);

  return (
    <>
      <BrandHeader brand={brand} />

      <div className="sources-row">
        <span className="sources-label">{t('decision.reportLabel')}</span>
        <div className="signal-quality" style={{ color: signal.color }}>
          <div className={`sq-dot ${brand.signalQuality}`} />
          {t('insight.signalWithQuality', {
            quality: t(`signalQuality.${brand.signalQuality}`),
          })}
        </div>
      </div>

      {/* Le due griglie erano stili inline, come nel riferimento (`55fr 45fr` e
          `1fr 1fr`). Sotto i 900 px vanno entrambe a colonna singola, e uno
          stile inline si batte solo a colpi di `!important`: i valori sono
          passati tali e quali a `.l2-split` e `.platform-grid` in app.css.

          La colonna di destra può restare vuota — un brand con la sola
          piattaforma primaria non disponibile — e in quel caso la griglia a due
          colonne lascerebbe un buco: senza entrambe le colonne si rende una
          sezione semplice. */}
      {diagnosis && platformDetail && isVisible(platformDetail) ? (
        <div className="l2-split">
          <Section section={diagnosis} style={{ marginBottom: 0 }} />
          <Section section={platformDetail} style={{ marginBottom: 0 }} />
        </div>
      ) : (
        <>
          {diagnosis ? <Section section={diagnosis} /> : null}
          {platformDetail ? <Section section={platformDetail} /> : null}
        </>
      )}

      {economicExposure ? <Section section={economicExposure} /> : null}
      {remediation ? <Section section={remediation} /> : null}

      <div
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 13,
          fontStyle: 'italic',
          color: 'var(--text-3)',
          lineHeight: 1.5,
          padding: '16px 0',
          borderTop: '1px solid var(--border-soft)',
        }}
      >
        {t('decision.disclaimer')}
      </div>
    </>
  );
}

/** Una sezione senza card e senza extra non si rende, titolo compreso. */
function isVisible(section: DecisionSection): boolean {
  return section.cards.length > 0 || section.extras !== null;
}

function Section({
  section,
  style,
}: {
  section: DecisionSection;
  style?: CSSProperties;
}): ReactNode {
  const { t } = useTranslation();
  if (!isVisible(section)) return null;

  return (
    <div className="l2-section" style={style}>
      <div className="l2-section-title">{t(section.titleKey)}</div>
      {section.extras}
      {section.cards.map((card) => (
        <Accordion
          key={card.id}
          num={card.num}
          title={t(`decision.card.${card.id}.title`)}
          subtitle={t(`decision.card.${card.id}.subtitle`)}
        >
          {card.body}
        </Accordion>
      ))}
    </div>
  );
}
